<script setup lang="ts">
import { computed } from 'vue';

interface Apartment {
  listing_id: number;
  title: string;
  cover_photo: { url: string; alt?: string };
  location?: string;
  capacity?: number | string;
  rooms?: number | string;
  area_sqm?: number;
  view_type?: 'sea' | 'panoramic' | 'mountain';
  floor?: number | string;
  description?: string;
  extra_bed_available?: boolean;
}

interface Quote {
  available: boolean;
  reason?: 'capacity' | 'dates_booked' | 'invalid_dates' | 'no_pricing';
  nights: number;
  tier: 'daily' | 'weekly';
  subtotalUsd: number;
  cleaningUsd: number;
  prepaymentUsd: number;
  totalUsd: number;
  poolIncluded: boolean;
  extraGuestUsd?: number;
}

const props = withDefaults(
  defineProps<{
    mode: 'catalog' | 'quote';
    apartment: Apartment;
    catalogPrice?: number | null;
    quote?: Quote;
    guestFacts?: { capacity: number; threshold: number; extraGuestUsd?: number } | null;
    locale: string;
    whatsappNumber?: string;
  }>(),
  {
    catalogPrice: null,
    quote: undefined,
    guestFacts: null,
    whatsappNumber: '',
  },
);

const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { trackEvent } = useAnalytics();

// Preserve booking context (dates + guests) when navigating from /search
// into the apartment detail page so QuoteWidget pre-fills instead of resetting.
const detailsPath = computed(() => {
  const path = localePath(`/apartment/${props.apartment.listing_id}`);
  const q: Record<string, string> = {};
  if (typeof route.query.from === 'string' && route.query.from) q.from = route.query.from;
  if (typeof route.query.to === 'string' && route.query.to) q.to = route.query.to;
  const g = route.query.guests;
  if (g && Number(g) > 0) q.guests = String(g);
  return Object.keys(q).length ? { path, query: q } : path;
});

const isUnavailable = computed(
  () => props.mode === 'quote' && props.quote?.available === false,
);

const {
  capacityNum,
  baseGuests,
  extraGuests,
  totalGuests,
  extraGuestUsd,
  roomsNum,
  floorNum,
  isHighFloor,
  viewLabel,
  areaSqmRounded,
} = useApartmentFacts(
  () => props.apartment,
  () => props.guestFacts,
);

const extraGuestTitle = computed(() =>
  extraGuestUsd.value
    ? t('apartmentRow.facts.extraGuestHintPriced', { price: extraGuestUsd.value })
    : t('apartmentRow.facts.extraGuestHint'),
);

const unavailableReason = computed(() => {
  if (!props.quote || !props.quote.reason) return '';
  const capacity = capacityNum.value ?? 0;
  return t(`quote.unavailable.${props.quote.reason}`, {
    nights: props.quote.nights,
    capacity,
  });
});
</script>

<template>
  <article
    class="group relative overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:border-slate-300 hover:shadow-lg dark:border-slate-700/60 dark:bg-slate-800 dark:hover:border-slate-600"
    :class="isUnavailable ? 'apartment-row--unavailable' : ''"
  >
    <div
      class="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)_auto]"
    >
      <!-- Image -->
      <NuxtLink
        :to="detailsPath"
        class="relative block h-44 overflow-hidden md:h-auto md:min-h-[96px]"
        :aria-label="apartment.title"
      >
        <NuxtImg
          :src="apartment.cover_photo.url"
          :alt="apartment.cover_photo.alt ?? apartment.title"
          width="540"
          height="400"
          format="webp"
          loading="lazy"
          sizes="sm:100vw md:180px"
          class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          :class="isUnavailable ? 'saturate-50' : ''"
        />
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5"
        />
      </NuxtLink>

      <!-- Content: 3 tight lines (badges → title → facts) -->
      <div class="flex min-w-0 flex-col justify-center gap-1 px-4 py-2 md:border-s md:border-slate-100 md:dark:border-slate-700/50">
        <!-- Line 1: badges only -->
        <div
          v-if="viewLabel || isHighFloor || (mode === 'quote' && quote?.poolIncluded && !isUnavailable)"
          class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5"
        >
          <span
            v-if="viewLabel"
            class="inline-flex items-center rounded-full bg-amber-100 px-2 md:px-1.5 font-body text-[10px] md:text-[9px] font-semibold uppercase tracking-[0.14em] leading-[18px] md:leading-[15px] text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
          >
            {{ viewLabel }}
          </span>
          <span
            v-if="isHighFloor && floorNum"
            class="inline-flex items-center rounded-full bg-slate-100 px-2 md:px-1.5 font-body text-[10px] md:text-[9px] font-semibold uppercase tracking-[0.14em] leading-[18px] md:leading-[15px] text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
          >
            {{ t('apartmentRow.badges.floor', { floor: floorNum }) }}
          </span>
          <span
            v-if="mode === 'quote' && quote?.poolIncluded && !isUnavailable"
            class="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 md:px-1.5 font-body text-[10px] md:text-[9px] font-semibold uppercase tracking-[0.14em] leading-[18px] md:leading-[15px] text-teal-800 dark:bg-teal-500/15 dark:text-teal-200"
          >
            <Icon name="lucide:waves" class="size-2.5" aria-hidden="true" />
            {{ t('quote.poolIncluded') }}
          </span>
        </div>

        <!-- Line 2: Title -->
        <NuxtLink
          :to="detailsPath"
          class="line-clamp-1 font-display text-base italic font-medium leading-tight text-slate-900 transition-colors duration-300 group-hover:text-red-500 dark:text-white"
        >
          {{ apartment.title }}
        </NuxtLink>

        <!-- Line 2: Facts -->
        <div
          v-if="baseGuests || roomsNum || areaSqmRounded || (mode === 'quote' && quote)"
          class="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-body text-[13px] md:text-[12px] leading-tight text-slate-500 dark:text-slate-400"
        >
          <span v-if="baseGuests" class="inline-flex items-center gap-1">
            <Icon name="lucide:users" class="size-3 text-slate-400" aria-hidden="true" />
            <span>{{ baseGuests }}</span>
            <span
              v-if="extraGuests > 0"
              :title="extraGuestTitle"
              class="inline-flex items-center rounded bg-amber-100/70 px-1 font-semibold tabular-nums text-amber-700 cursor-help dark:bg-amber-400/15 dark:text-amber-300"
            >+{{ extraGuests }}</span>
            <span>{{ t('apartmentRow.facts.guestsWord', totalGuests ?? baseGuests) }}</span>
          </span>
          <span v-if="baseGuests && (roomsNum || areaSqmRounded)" aria-hidden="true" class="text-amber-500/60">·</span>
          <span v-if="roomsNum" class="inline-flex items-center gap-1">
            <Icon name="lucide:bed-single" class="size-3 text-slate-400" aria-hidden="true" />
            {{ t('apartmentRow.facts.rooms', { count: roomsNum }, roomsNum) }}
          </span>
          <span v-if="roomsNum && areaSqmRounded" aria-hidden="true" class="text-amber-500/60">·</span>
          <span v-if="areaSqmRounded" class="inline-flex items-center gap-1">
            <Icon name="lucide:ruler" class="size-3 text-slate-400" aria-hidden="true" />
            {{ t('apartmentRow.facts.area', { value: areaSqmRounded }) }}
          </span>
          <template v-if="mode === 'quote' && quote && !isUnavailable">
            <span v-if="baseGuests || roomsNum || areaSqmRounded" aria-hidden="true" class="text-amber-500/60">·</span>
            <span class="inline-flex items-center gap-1 uppercase tracking-[0.15em] text-[11px]">
              {{ quote.nights }} {{ t('quote.nights').toLowerCase() }}
            </span>
          </template>
        </div>

        <!-- Line 3 (unavailable only): inline reason -->
        <div
          v-if="isUnavailable"
          class="flex items-baseline gap-2 font-body text-[13px]"
        >
          <span
            class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:bg-slate-700/60 dark:text-slate-300"
          >
            {{ t('apartmentRow.unavailable.heading') }}
          </span>
          <span class="text-slate-600 dark:text-slate-300 line-clamp-1">
            {{ unavailableReason }}
          </span>
        </div>
      </div>

      <!-- Price + CTA column (hidden for unavailable rows on md+) -->
      <div
        v-if="!isUnavailable"
        class="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-1.5 md:justify-end md:gap-3 md:px-4 md:py-2 md:border-s md:border-t-0 dark:border-slate-700/50"
      >
        <template v-if="mode === 'catalog'">
          <div
            v-if="catalogPrice != null"
            class="flex items-baseline gap-1 font-body"
          >
            <span class="text-[10px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500">
              {{ t('apartmentList.price.startFrom') }}
            </span>
            <span class="font-display italic text-amber-500 text-base leading-none">$</span>
            <span class="font-display italic text-amber-500 text-xl font-semibold tabular-nums leading-none">
              {{ catalogPrice }}
            </span>
            <span class="text-[11px] text-slate-400 dark:text-slate-500">
              {{ t('apartmentList.price.perNight') }}
            </span>
          </div>
          <NuxtLink
            :to="detailsPath"
            :aria-label="t('apartmentList.detailsAria', { title: apartment.title })"
            class="flex size-9 items-center justify-center rounded-full bg-slate-100 text-slate-900 shadow-sm transition-all duration-300 hover:bg-red-500 hover:text-white hover:shadow-red-500/30 dark:bg-slate-700 dark:text-white"
          >
            <Icon
              name="lucide:arrow-up-right"
              class="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              aria-hidden="true"
            />
          </NuxtLink>
        </template>

        <template v-else-if="mode === 'quote' && quote">
          <div class="flex items-baseline gap-1 font-body">
            <span class="text-[10px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500">
              {{ t('apartmentRow.price.total') }}
            </span>
            <span class="font-display italic text-amber-500 text-base leading-none">$</span>
            <span class="font-display italic text-amber-500 text-xl font-semibold tabular-nums leading-none">
              {{ quote.totalUsd }}
            </span>
          </div>
          <NuxtLink
            :to="detailsPath"
            :aria-label="t('apartmentRow.cta.book')"
            class="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-red-500 px-3.5 py-1 font-body text-[9px] font-semibold uppercase tracking-[0.14em] text-white shadow-md shadow-red-500/25 transition-all duration-300 hover:bg-red-600 hover:shadow-red-500/40 sm:gap-1.5 sm:px-4 sm:py-2 sm:text-[11px] sm:tracking-[0.18em]"
            @click.stop="trackEvent('card_book_click', { listing_id: apartment.listing_id, locale })"
          >
            {{ t('apartmentRow.cta.book') }}
            <Icon name="lucide:arrow-up-right" class="size-3 sm:size-3" aria-hidden="true" />
          </NuxtLink>
        </template>
      </div>
    </div>
  </article>
</template>

<style scoped>
.apartment-row--unavailable {
  background-image: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 10px,
    rgba(148, 163, 184, 0.06) 10px,
    rgba(148, 163, 184, 0.06) 11px
  );
  opacity: 0.78;
}
.apartment-row--unavailable:hover {
  opacity: 0.88;
}
</style>
